'use client';

import Link from '@eliadmin/core/src/components/link';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import type { UseFormReturn } from 'react-hook-form';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2, boolean as __eliadmin_zod_3 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import { getGdprUrl, getTosUrl } from '@eliadmin/core/src/utils/gdpr-tos/get-tos-gdpr-url';
import { Paragraph } from '../ui/paragraph';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { useQuery } from '@tanstack/react-query';
import { Heading } from '../ui/heading';
import { RegisterSuccess } from './register-success';

import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { Field } from '@eliadmin/core/src/components/form/field';
import { MdDomain, MdLockOutline, MdOutlineEmail } from 'react-icons/md';
import { RiAccountCircleLine } from 'react-icons/ri';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';

function Separator({ className }: { className?: string }) {
  return <div className={cn('h-[1px] w-full bg-border', className)} />;
}

export type RegisterFormValues = z.infer<ReturnType<typeof registerSchema>>;

export const registerSchema = (VM: FormValidationMessages, passwordRuleMessage?: string) =>
  __eliadmin_zod_0({
      name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: __eliadmin_zod_1().min(12, VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_1()
        .min(6, VM.MIN_LENGTH_VALIDATION_MESSAGE(6))
        .max(64, VM.MAX_LENGTH_VALIDATION_MESSAGE(64))
        .regex(/[A-Z]/, passwordRuleMessage)
        .regex(/[0-9]/, passwordRuleMessage),
      password_confirmation: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),

      address: __eliadmin_zod_1().optional(),
      street_number: __eliadmin_zod_1().optional(),
      postcode: __eliadmin_zod_1().optional(),
      city: __eliadmin_zod_1().optional(),
      country: __eliadmin_zod_0({
          id: __eliadmin_zod_1().or(__eliadmin_zod_2()).optional(),
        })
        .optional(),

      company_buy: __eliadmin_zod_3().optional(),
      company: __eliadmin_zod_1().optional(),
      ico: __eliadmin_zod_1().optional(),
      dic: __eliadmin_zod_1().optional(),
      ic_dph: __eliadmin_zod_1().optional(),
      email_company: __eliadmin_zod_1().optional(),
      address_company: __eliadmin_zod_1().optional(),
      street_number_company: __eliadmin_zod_1().optional(),
      postcode_company: __eliadmin_zod_1().optional(),
      city_company: __eliadmin_zod_1().optional(),
      country_company: __eliadmin_zod_0({
          id: __eliadmin_zod_1(),
        })
        .optional(),

      gdpr: __eliadmin_zod_3().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: __eliadmin_zod_3().optional(),

      newsletter_return_url: __eliadmin_zod_1(),
      tos: __eliadmin_zod_3().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': __eliadmin_zod_1().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

export function RegisterForm({
  initialSocialAuthProviders,
  onShowSignIn,
  inDialog = false,
}: {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  onShowSignIn?: () => void;
  inDialog?: boolean;
}) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);
  const defaultCountry = useGlobalStore(s => s.generalLayout.country);

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const form = useForm<z.infer<ReturnType<typeof registerSchema>>>({
    resolver: zodResolver(registerSchema(formValidationMessages, t('text2349'))),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,

      address: '',
      street_number: '',
      postcode: '',
      city: '',
      country: {
        id: defaultCountry?.id ?? '',
      },

      company_buy: false,
      company: '',
      ico: '',
      dic: '',
      ic_dph: '',
      email_company: '',
      address_company: '',
      street_number_company: '',
      postcode_company: '',
      city_company: '',
      country_company: undefined,

      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
    },
  });

  const router = useRouter();

  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = form.handleSubmit(async data => {
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    try {

      const resp = await register({ ...data,  locale });

      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });

      if (inDialog) setIsSuccess(true);

      if (!inDialog) {
        toast({
          variant: 'success',
          title: 'Úspešne ste sa zaregistrovali',
        });
        router.push(ROUTES.PROFILE());
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
      }
    }
  });

  if (isSuccess) {
    return <RegisterSuccess inDialog={inDialog} />;
  }

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex w-full flex-col items-center justify-center gap-6',
          'ea-register-form',
          !inDialog && 'py-8 sm:py-12 lg:py-14',
        )}
        onSubmit={handleSubmit}
      >
        <div className="flex w-full flex-col items-center justify-center gap-6">
          <Heading title={t('text261')} className={{ container: 'text-center' }} />
          <Paragraph className="text-center text-[#666666]">
            {t('text623')}&nbsp;
            <button
              type="button"
              className={cn(
                'cursor-pointer font-bold text-walnut transition-all hover:opacity-70',
              )}
              onClick={inDialog ? onShowSignIn : () => router.push(ROUTES.SIGN_IN())}
            >
              {t('text624')}
            </button>
          </Paragraph>
        </div>

        <SocialAuthButtons
          className="ea-register-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
          newsletterAccepted={form.watch('newsletter' as any)}
          newsletterReturnUrl={form.watch('newsletter_return_url' as any)}
        />

        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-6',
            'ea-register-form__content',
          )}
        >
          <div className="grid w-full grid-cols-12 gap-3 md:gap-4">
            <FormTitle name={t('text385')} className="col-span-12" />
            <Field
              required
              type="email"
              name="email"
              placeholder={'E-mail'}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full' }}
              before={<MdOutlineEmail size={20} className="text-[#9CA3AF]" />}
            />
            <Field
              required
              type="password"
              name="password"
              placeholder={`${t('text191')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              before={<MdLockOutline size={20} className="text-[#9CA3AF]" />}
              description={t('text2349')}
            />
            <Field
              required
              type="password"
              name="password_confirmation"
              placeholder={`${t('text3183')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              before={<MdLockOutline size={20} className="text-[#9CA3AF]" />}
            />
          </div>

          <Separator />

          <div className="grid w-full grid-cols-12 gap-3 md:gap-4">
            <FormTitle name={t('text3560')} className="col-span-12" />
            <FormField
              control={form.control}
              name="company_buy"
              render={({ field }) => (
                <FormItem
                  className={cn('ea-register-form__company-type-field', 'col-span-12 w-full')}
                >
                  <FormControl>
                    <div className="grid w-full grid-cols-2 gap-3">
                      <button
                        type="button"
                        aria-pressed={field.value === false}
                        onClick={() => field.onChange(false)}
                        className={cn(
                          'col-span-1 flex w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[4px] p-4 text-sm font-medium tracking-tight text-black sm:text-base',
                          field.value === false
                            ? 'border-2 border-walnut bg-walnut/10'
                            : 'border border-border',
                        )}
                      >
                        <RiAccountCircleLine size={24} className="!text-walnut" aria-hidden />
                        <span>{t('text2234')}</span>
                      </button>

                      <button
                        type="button"
                        aria-pressed={field.value === true}
                        onClick={() => field.onChange(true)}
                        className={cn(
                          'col-span-1 flex w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[4px] p-4 text-sm font-medium tracking-tight text-black sm:text-base',
                          field.value === true
                            ? 'border-2 border-walnut bg-walnut/10'
                            : 'border border-border',
                        )}
                      >
                        <MdDomain size={24} className="!text-walnut" aria-hidden />
                        <span>{t('text1432')}</span>
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.watch('company_buy') === true ? (
              <CompanyFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            ) : (
              <PersonFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            )}
          </div>

          <Separator />

          <div className="col-span-12 grid w-full grid-cols-12 flex-col items-start justify-start gap-4 gap-x-3 gap-y-4">
            <FormTitle name={t('text803')} className="col-span-12" />
            {form.watch('company_buy') === true && (
              <>
                <Field
                  name="name"
                  label={`${t('text33')} *`}
                  placeholder={`${t('text33')}`}
                  control={form.control}
                  className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
                />
                <Field
                  name="surname"
                  label={`${t('text34')} *`}
                  placeholder={`${t('text34')}`}
                  control={form.control}
                  className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
                />
              </>
            )}

            {/* {form.watch('company_buy') === true && (
              <Field
                name="email_company"
                type="email"
                label={'Email *'}
                placeholder={'Email'}
                control={form.control}
                className={{ wrapper: 'col-span-12 w-full' }}
              />
            )} */}
            {/* <Field
              name="name"
              label={`${t('text33')} *`}
              placeholder={`${t('text33')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            />
            <Field
              name="surname"
              label={`${t('text34')} *`}
              placeholder={`${t('text34')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            /> */}
            <Field
              name="phone"
              label={`${t('text36')} *`}
              placeholder={`${t('text36')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full' }}
              type="tel"
            />
            {/* {form.watch('company_buy') === true && (
              <>
                <Separator className="col-span-12 my-2 w-full" />
                <div className="col-span-12 grid w-full grid-cols-12 flex-col items-start justify-start gap-4 gap-x-2 gap-y-4 md:gap-x-3">
                  <FormTitle name="Kontaktná osoba" className="col-span-12" />
                </div>
              </>
            )} */}
          </div>
        </div>
        <Separator />
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-4',
            'ea-register-form__footer',
          )}
        >
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row flex-wrap items-center justify-start gap-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="font-medium">
                  {t('text264')}{' '}
                  <Link
                    href={getTosUrl(locale)}
                    className="font-bold text-primary underline"
                    prefetch={false}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('text265')}
                  </Link>
                </FormLabel>
                <FormMessage className="w-full" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row flex-wrap items-center justify-start gap-0',
                  'ea-register-form__gdpr-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <Link
                    href={getGdprUrl()}
                    className="font-bold text-primary underline"
                    prefetch={false}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('text364')}
                  </Link>
                </FormLabel>
                <FormMessage className="w-full" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              // Was `hidden`, so a failed captcha never showed its message; `empty:hidden` keeps the
              // row out of the layout until there is an error to render.
              <FormItem required className={cn('w-full empty:hidden')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="mt-2 flex w-full items-center justify-center">
            <Button
              type="submit"
              className={cn('h-12 w-full', 'ea-register-form__submit-button')}
              isLoading={form.formState.isSubmitting}
            >
              {t('text758')}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

function FormTitle({ name, className }: { name: string; className?: string }) {
  return (
    <Heading
      className={{
        title: 'text-center',
        container: cn('col-span-12 w-full items-center justify-center text-center', className),
      }}
      title={name}
      tag="h5"
    />
  );
}

function PersonFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        name="name"
        placeholder={`${t('text33')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="surname"
        placeholder={`${t('text34')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="address"
        placeholder={`${t('text157')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        name="street_number"
        placeholder={`${t('text438')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        name="postcode"
        placeholder={`${t('text40')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-3' }}
      />
      <Field
        name="city"
        placeholder={`${t('text39')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-9' }}
      />
      <Field
        name="country.id"
        type="select"
        placeholder={`${t('text519')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}

function CompanyFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        required
        name="company"
        placeholder={`${t('text1188')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        required
        name="ico"
        placeholder={`${t('text42')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="dic"
        placeholder={`${t('text43')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="ic_dph"
        placeholder={`${t('text44')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        required
        name="address_company"
        placeholder={`${t('text38')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        required
        name="street_number_company"
        placeholder={`${t('text438')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        name="postcode_company"
        placeholder={`${t('text40')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-3' }}
      />
      <Field
        name="city_company"
        placeholder={`${t('text39')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-9' }}
      />
      <Field
        required
        control={form.control}
        type="select"
        name="country_company.id"
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}
